import Keycloak from "keycloak-js";

import { unregisterNotificationDeviceBestEffort } from "@/core/notifications/notification-devices";
import { useSessionStore } from "@/core/stores/session.store";

import { hasAppAccess } from "./roles";

/**
 * Adaptateur Keycloak — point d'entrée unique de l'authentification Flash Pay.
 *
 * La configuration vient des variables d'environnement VITE_KEYCLOAK_*.
 * Les tokens sont synchronisés dans le session store zustand : la couche API
 * (kyInstance) injecte déjà le Bearer depuis ce store, elle n'a pas à connaître
 * Keycloak.
 */

const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
});

/** Intervalle (s) entre deux tentatives de rafraîchissement du token. */
const TOKEN_REFRESH_INTERVAL_SECONDS = 30;
/** Le token est rafraîchi s'il expire dans moins de N secondes. */
const TOKEN_MIN_VALIDITY_SECONDS = 60;

function syncSessionFromKeycloak() {
  useSessionStore.getState().setSession({
    accessToken: keycloak.token ?? null,
    refreshToken: keycloak.refreshToken ?? null,
    // NB : tokenParsed.exp est un timestamp epoch ABSOLU (secondes), pas une durée.
    expiresIn: keycloak.tokenParsed?.exp ?? null,
    userInfo: (keycloak.tokenParsed as Record<string, unknown> | undefined) ?? null,
  });
}

let refreshTimer: ReturnType<typeof setInterval> | null = null;

function startTokenRefresh() {
  if (refreshTimer) return;
  refreshTimer = setInterval(() => {
    keycloak
      .updateToken(TOKEN_MIN_VALIDITY_SECONDS)
      .then((refreshed) => {
        if (refreshed) syncSessionFromKeycloak();
      })
      .catch(() => {
        // Session expirée côté IdP : on nettoie ; la redirection vers le
        // SSO est assurée par RequireAuth (routes protégées) ou par le
        // hook 401 de kyInstance.
        stopTokenRefresh();
        useSessionStore.getState().clearSession();
      });
  }, TOKEN_REFRESH_INTERVAL_SECONDS * 1000);
}

function stopTokenRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

export interface InitKeycloakOptions {
  /** Appelé quand l'utilisateur est authentifié (login ou session restaurée). */
  onAuthenticated?: () => void;
}

/**
 * Initialise Keycloak. `check-sso` restaure une session existante sans
 * rediriger l'utilisateur non connecté (les routes protégées décideront).
 */
export async function initKeycloak(options: InitKeycloakOptions = {}): Promise<boolean> {
  const authenticated = await keycloak.init({
    onLoad: "check-sso",
    pkceMethod: "S256",
    checkLoginIframe: false,
  });

  if (authenticated) {
    syncSessionFromKeycloak();
    if (!hasAppAccess()) {
      // Compte sans accès à l'app (self-service `default-roles-flash-pay`
      // ou `flashpay-customer` seul) : déconnexion immédiate, il ne doit
      // jamais atteindre le dashboard.
      void keycloakLogout();
      return false;
    }
    startTokenRefresh();
    options.onAuthenticated?.();
  }

  keycloak.onAuthLogout = () => {
    stopTokenRefresh();
    useSessionStore.getState().clearSession();
  };

  return authenticated;
}

export function keycloakLogin() {
  void keycloak.login();
}

export async function keycloakLogout() {
  await unregisterNotificationDeviceBestEffort();
  stopTokenRefresh();
  useSessionStore.getState().clearSession();
  void keycloak.logout();
}

export function isKeycloakAuthenticated(): boolean {
  return Boolean(keycloak.authenticated);
}

export default keycloak;
